import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight, Loader2, Merge, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useConfirm } from '../../components/shared/ConfirmDialog';
import {
  SuspectedDuplicate,
  SuspectedDuplicateStatus,
  UserSummary,
  duplicatesApi,
} from '../../api/duplicates';
import { InfoCard } from './components/InfoCard';
import { MergeDialog, MergeTarget } from './components/MergeDialog';
import { UserSummaryCard, apiErrorMessage, formatDate, tgHandle, userTitle } from './components/shared';

interface DuplicatesPageProps {
  isDark: boolean;
}

const STATUS_FILTERS: Array<{ value: SuspectedDuplicateStatus; label: string }> = [
  { value: 'open', label: 'Открытые' },
  { value: 'merged', label: 'Склеены' },
  { value: 'dismissed', label: 'Не дубль' },
];

const cardClass = (isDark: boolean) =>
  cn(
    'rounded-2xl border-0 p-5',
    isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
  );

export default function DuplicatesPage({ isDark }: DuplicatesPageProps) {
  const [tab, setTab] = useState<'suspected' | 'search'>('suspected');
  const [mergeTarget, setMergeTarget] = useState<MergeTarget | null>(null);
  // Счётчик, по которому вкладки перезагружают данные после склейки
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div className="space-y-6">
      <div>
        <h1
          className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]',
          )}
        >
          Дубли аккаунтов
        </h1>
        <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Склейка дубля, созданного входом через Telegram, с основным аккаунтом человека
        </p>
      </div>

      <InfoCard isDark={isDark} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'suspected' | 'search')}>
        <TabsList className={cn('rounded-xl p-1', isDark ? 'bg-[#1A1A22]' : 'bg-white')}>
          <TabsTrigger value="suspected" className="rounded-lg">
            Возможные дубли
          </TabsTrigger>
          <TabsTrigger value="search" className="rounded-lg">
            Поиск и ручная склейка
          </TabsTrigger>
        </TabsList>

        <TabsContent value="suspected" className="mt-4">
          <SuspectedTab isDark={isDark} reloadKey={reloadKey} onMerge={setMergeTarget} />
        </TabsContent>
        <TabsContent value="search" className="mt-4">
          <SearchTab isDark={isDark} reloadKey={reloadKey} onMerge={setMergeTarget} />
        </TabsContent>
      </Tabs>

      <MergeDialog
        isDark={isDark}
        target={mergeTarget}
        onClose={() => setMergeTarget(null)}
        onMerged={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}

// ─── Вкладка «Возможные дубли» ────────────────────────────────────────────────

interface TabProps {
  isDark: boolean;
  reloadKey: number;
  onMerge: (target: MergeTarget) => void;
}

function SuspectedTab({ isDark, reloadKey, onMerge }: TabProps) {
  const confirm = useConfirm();
  const [status, setStatus] = useState<SuspectedDuplicateStatus>('open');
  const [items, setItems] = useState<SuspectedDuplicate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await duplicatesApi.listSuspected(status));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось загрузить возможные дубли'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleDismiss = async (item: SuspectedDuplicate) => {
    const ok = await confirm({
      title: 'Отметить «Не дубль»?',
      description: `Пара «${userTitle(item.newUser)}» ↔ «${userTitle(item.previousUser)}» уйдёт из открытых. Аккаунты не меняются.`,
      confirmLabel: 'Не дубль',
    });
    if (!ok) return;
    setDismissingId(item.id);
    try {
      await duplicatesApi.dismiss(item.id);
      toast.success('Отмечено: не дубль');
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось отметить'));
    } finally {
      setDismissingId(null);
    }
  };

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant="outline"
            onClick={() => setStatus(f.value)}
            className={cn(
              'rounded-xl',
              status === f.value
                ? 'bg-[#C9A227] border-[#C9A227] text-white hover:bg-[#C9A227]/90 hover:text-white'
                : isDark
                ? 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
                : '',
            )}
          >
            {f.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className={cn('ml-auto', muted)}
        >
          <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
          Обновить
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
        </div>
      ) : items.length === 0 ? (
        <Card className={cardClass(isDark)}>
          <p className={cn('text-sm text-center py-8', muted)}>
            {status === 'open' ? 'Открытых возможных дублей нет' : 'Список пуст'}
          </p>
        </Card>
      ) : (
        items.map((item) => (
          <Card key={item.id} className={cn(cardClass(isDark), 'space-y-3')}>
            <p className={cn('text-xs', muted)}>Замечено: {formatDate(item.detectedAt)}</p>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
              <UserSummaryCard user={item.newUser} role="Дубль (Telegram)" tone="duplicate" isDark={isDark} />
              <div className="flex items-center justify-center">
                <ArrowLeftRight className={cn('h-5 w-5 rotate-90 lg:rotate-0', muted)} />
              </div>
              <UserSummaryCard user={item.previousUser} role="Основной" tone="main" isDark={isDark} />
            </div>
            {item.status === 'open' && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleDismiss(item)}
                  disabled={dismissingId === item.id}
                  className={cn(
                    isDark ? 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white' : '',
                  )}
                >
                  {dismissingId === item.id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <X className="h-4 w-4 mr-2" />
                  )}
                  Не дубль
                </Button>
                <Button
                  onClick={() => onMerge({ duplicateId: item.newUser.id, mainId: item.previousUser.id })}
                  className="bg-[#C9A227] hover:bg-[#C9A227]/90 text-white"
                >
                  <Merge className="h-4 w-4 mr-2" />
                  Склеить…
                </Button>
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );
}

// ─── Вкладка «Поиск и ручная склейка» ─────────────────────────────────────────

function SearchTab({ isDark, reloadKey, onMerge }: TabProps) {
  const [query, setQuery] = useState('');
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [results, setResults] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [duplicate, setDuplicate] = useState<UserSummary | null>(null);
  const [main, setMain] = useState<UserSummary | null>(null);

  const runSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      toast.error('Введите хотя бы 2 символа');
      return;
    }
    setSearching(true);
    try {
      setResults(await duplicatesApi.searchUsers(trimmed));
      setLastQuery(trimmed);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось выполнить поиск'));
    } finally {
      setSearching(false);
    }
  }, []);

  // После успешной склейки сбрасываем выбор и обновляем выдачу (у дубля сменился статус)
  useEffect(() => {
    if (reloadKey === 0) return;
    setDuplicate(null);
    setMain(null);
    if (lastQuery) runSearch(lastQuery);
  }, [reloadKey]);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const sameUser = !!duplicate && !!main && duplicate.id === main.id;
  const noTelegram = !!duplicate && !duplicate.telegramId;

  const pickBtn = (active: boolean, activeCls: string) =>
    cn(
      'rounded-xl',
      active
        ? activeCls
        : isDark
        ? 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
        : '',
    );

  return (
    <div className="space-y-4">
      <Card className={cn(cardClass(isDark), 'space-y-3')}>
        <form
          className="flex flex-col sm:flex-row gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch(query);
          }}
        >
          <div className="relative flex-1">
            <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4', muted)} />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Номер, @telegram, telegram id, email или username"
              className={cn('pl-9', isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
            />
          </div>
          <Button type="submit" disabled={searching} className="bg-[#C9A227] hover:bg-[#C9A227]/90 text-white">
            {searching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Найти
          </Button>
        </form>
        <p className={cn('text-xs', muted)}>
          Номер можно вводить частью цифр (например, 901234567). Найдите оба аккаунта человека и
          отметьте, какой из них дубль (с Telegram), а какой — основной.
        </p>
      </Card>

      {/* Выбранная пара */}
      <Card className={cn(cardClass(isDark), 'space-y-3')}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {duplicate ? (
            <UserSummaryCard
              user={duplicate}
              role="Дубль (Telegram)"
              tone="duplicate"
              isDark={isDark}
              footer={
                <Button size="sm" variant="ghost" onClick={() => setDuplicate(null)} className={muted}>
                  <X className="h-4 w-4 mr-1" /> Убрать
                </Button>
              }
            />
          ) : (
            <EmptySlot isDark={isDark} text="Дубль (Telegram) не выбран" />
          )}
          {main ? (
            <UserSummaryCard
              user={main}
              role="Основной"
              tone="main"
              isDark={isDark}
              footer={
                <Button size="sm" variant="ghost" onClick={() => setMain(null)} className={muted}>
                  <X className="h-4 w-4 mr-1" /> Убрать
                </Button>
              }
            />
          ) : (
            <EmptySlot isDark={isDark} text="Основной аккаунт не выбран" />
          )}
        </div>
        {sameUser && <p className="text-sm text-red-500">Дубль и основной — один и тот же аккаунт.</p>}
        {noTelegram && (
          <p className="text-sm text-amber-600">
            У выбранного дубля нет входа через Telegram — переносить нечего.
          </p>
        )}
        <div className="flex justify-end">
          <Button
            disabled={!duplicate || !main || sameUser}
            onClick={() => duplicate && main && onMerge({ duplicateId: duplicate.id, mainId: main.id })}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90 text-white"
          >
            <Merge className="h-4 w-4 mr-2" />
            Склеить…
          </Button>
        </div>
      </Card>

      {/* Результаты поиска */}
      {lastQuery !== null && (
        <Card className={cn(cardClass(isDark), 'p-0 overflow-hidden')}>
          <div className={cn('px-5 py-3 text-sm border-b', muted, isDark ? 'border-slate-700' : 'border-slate-100')}>
            Найдено: {results.length}
            {results.length >= 20 ? ' (показаны первые 20 — уточните запрос)' : ''}
          </div>
          {results.length === 0 ? (
            <p className={cn('text-sm text-center py-8', muted)}>Ничего не найдено</p>
          ) : (
            <ul className={cn('divide-y', isDark ? 'divide-slate-700' : 'divide-slate-100')}>
              {results.map((u) => {
                const unavailable = u.isDeleted || !!u.mergedIntoUserId;
                return (
                  <li key={u.id} className="px-5 py-3 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className={cn('font-medium truncate', isDark ? 'text-white' : 'text-[#111111]')}>
                        {userTitle(u)}
                        {u.mergedIntoUserId && <span className="ml-2 text-xs text-slate-500">склеен</span>}
                        {u.isDeleted && <span className="ml-2 text-xs text-red-500">удалён</span>}
                      </p>
                      <p className={cn('text-xs break-all', muted)}>
                        {[
                          u.email,
                          u.phone,
                          u.telegramId ? `TG ${tgHandle(u)}` : null,
                          `создан ${formatDate(u.createdAt)}`,
                          u.isSubscribed ? `премиум до ${formatDate(u.subscribedEnd)}` : null,
                          u.hasPayments ? 'есть оплаты' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={unavailable}
                        onClick={() => setDuplicate(u)}
                        className={pickBtn(
                          duplicate?.id === u.id,
                          'bg-sky-500 border-sky-500 text-white hover:bg-sky-500/90 hover:text-white',
                        )}
                      >
                        Это дубль
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={unavailable}
                        onClick={() => setMain(u)}
                        className={pickBtn(
                          main?.id === u.id,
                          'bg-[#C9A227] border-[#C9A227] text-white hover:bg-[#C9A227]/90 hover:text-white',
                        )}
                      >
                        Это основной
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

function EmptySlot({ isDark, text }: { isDark: boolean; text: string }) {
  return (
    <div
      className={cn(
        'rounded-xl border-2 border-dashed p-6 flex items-center justify-center text-sm min-h-[120px]',
        isDark ? 'border-slate-700 text-slate-500' : 'border-slate-200 text-slate-400',
      )}
    >
      {text}
    </div>
  );
}
